/**
 * @file index.tsx
 *
 * @fileoverview Creates an empty placeholder for a resume box.
 */

import React, { useContext } from 'react';

import Link from '~/jobiqo/components/Link';
import { UserContext, UserContextInterface } from '~/jobiqo/context/user';
import { OrganizationProfile } from '~/jobiqo/types/types';
import { Button } from '~/vcl/atoms/01-forms/01-button';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import { EmptyElement } from '~/vcl/molecules/02-empty';

interface OrganizationEmptyBoxPlaceholderProps {
  /**
   * The field name to show the link to edit or add a new item.
   */
  field?: string;
  /**
   * The text to show in the empty box.
   */
  text: string;
  /**
   * The icon to show in the empty box.
   */
  icon: React.ReactNode;
  /**
   * The text for the button
   */
  buttonText: string;
  /**
   * The icon to show in the empty box button.
   */
  buttonIcon?: React.ReactNode;
  /**
   * The organization profile.
   */
  organization: OrganizationProfile;
  /**
   * if the box is a multiple box (e.g. contacts or locations)
   *
   * @default "false"
   */
  multiple?: boolean;
}

export const OrganizationEmptyBoxPlaceholder = ({
  field = '',
  text,
  icon,
  buttonText,
  buttonIcon,
  organization,
  multiple = false,
}: OrganizationEmptyBoxPlaceholderProps) => {
  const userContext: UserContextInterface = useContext(UserContext);

  return (
    <Link
      href={`/organization/edit/${organization.id}/${field}${
        multiple ? '/new' : ''
      }`}
      data-testid={`jbq-organization-box-placeholder-edit-${field}`}
      scroll={false}
    >
      <>
        <EmptyElement icon={icon} iconWidth="300" iconHeight="180">
          {userContext.user && organization.canEdit && (
            <>
              <Spacing />
              <p>{text}</p>
              <Spacing />
              <Button>
                {buttonIcon}
                <span>{buttonText}</span>
              </Button>
            </>
          )}
        </EmptyElement>
      </>
    </Link>
  );
};
